/**
 * CallListViewV2 — Toolbar on top, then Sidebar | Table inline (SmartSidebar + SmartTable).
 * Uses CallSidebarV2 and same table/pills as CallListView.
 */

import React from 'react';
import { Box, Button, Typography, Tooltip } from '@mui/material';
import {
  CallMade,
  CallReceived,
  CallMissed,
  HelpOutline,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCall } from '../../contexts/CallContext';
import { getCallStatusColor } from '../../utils/helper';
import { getStageConfig as STAGE_CFG, LEAD_THEME as LEAD_T } from '../../utils/lead_helper_v2';
import SmartAudioPlayer from '../../components/common/SmartAudioPlayer';
import AIInsightsSummaryCard, { getSummaryFromCall, AI_SUMMARY_CARD_WIDTH } from '../../components/calls/AIInsightsSummaryCard';
import EmptyCallsPageCard from './EmptyCallsPageCard';
import UnlinkedCallListView from './UnlinkedCallListView';
import CallSidebarV2 from './CallSidebarV2';
import {
  SmartTableTH,
  SmartTableTD,
  TABLE_THEME,
  TABLE_ROW_CLASS,
  TABLE_ROW_GLOBAL_CSS,
  TABLE_SCROLLBAR_CSS,
} from '../../components/common/SmartTable';

const T = TABLE_THEME;

function getSelectedSidebarMeta(statusCounts, selectedStatus) {
  const safe = (v) => (v == null ? 0 : Number(v) || 0);
  if (!statusCounts || !selectedStatus) return { label: 'Calls', count: 0 };

  // Direct sections
  if (statusCounts[selectedStatus]?.label) {
    return { label: statusCounts[selectedStatus].label, count: safe(statusCounts[selectedStatus].count) };
  }

  // Status children (ANS/NA/NC/MSD)
  if (statusCounts.status?.filters?.[selectedStatus]) {
    const node = statusCounts.status.filters[selectedStatus];
    return { label: node.label || selectedStatus, count: safe(node.count) };
  }

  // Lead stage children
  if (statusCounts.lead_stage?.filters?.[selectedStatus]) {
    const node = statusCounts.lead_stage.filters[selectedStatus];
    return { label: node.label || selectedStatus, count: safe(node.count) };
  }

  // Lead stage grandchildren like "closure_lost" or "new_not_contacted"
  const parts = String(selectedStatus).split('_');
  const stage = parts[0];
  const subKey = parts.slice(1).join('_');
  const stageNode = statusCounts.lead_stage?.filters?.[stage];
  const subNode = stageNode?.filters?.[subKey];
  if (subNode) {
    return { label: `${stageNode.label || stage} · ${subNode.label || subKey}`, count: safe(subNode.count) };
  }

  return { label: 'Calls', count: safe(statusCounts.all?.count) };
}

// ─── Pills (Leads-style) ────────────────────────────────────────────────────
function StagePill({ stage, label }) {
  const cfg = STAGE_CFG(stage);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: cfg.bg, color: cfg.text,
      fontSize: 11, fontWeight: 700, padding: '3px 9px 3px 7px',
      borderRadius: 99, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot }} />
      {label || stage || '—'}
    </span>
  );
}

function CallStatusPill({ status, label }) {
  const colors = getCallStatusColor(status || '-');
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: colors.background, color: colors.color,
      fontSize: 11, fontWeight: 600, padding: '3px 9px 3px 7px',
      borderRadius: 99, whiteSpace: 'nowrap',
      border: `1px solid ${colors.borderColor}`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: colors.color, flexShrink: 0 }} />
      {label || status || '—'}
    </span>
  );
}

// Direction: outgoing (green), incoming (blue), missed (red) — theme-aligned
function getCallTypeIcon(callType) {
  switch (callType) {
    case 'outgoing': return { icon: CallMade, color: LEAD_T.green, bg: LEAD_T.greenLt };
    case 'incoming': return { icon: CallReceived, color: '#1976d2', bg: '#e3f2fd' };
    case 'missed': return { icon: CallMissed, color: '#c62828', bg: '#ffebee' };
    default: return { icon: HelpOutline, color: T.muted, bg: T.borderL };
  }
}

function CallDirectionPill({ callType }) {
  const { icon: Icon, color, bg } = getCallTypeIcon(callType);
  const label = callType === 'outgoing' ? 'Outgoing' : callType === 'incoming' ? 'Incoming' : callType === 'missed' ? 'Missed' : 'Unknown';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: bg, color,
      fontSize: 11, fontWeight: 600, padding: '4px 10px',
      borderRadius: 99, whiteSpace: 'nowrap',
      border: `1px solid ${color}40`,
    }}>
      {React.createElement(Icon, { sx: { fontSize: 14 } })}
      {label}
    </span>
  );
}

// Agent pill — matches Leads `OwnerChip` exactly
function AgentChip({ name, assigned, onClick }) {
  const isUnassigned = !assigned || !name || name === 'Unassigned' || name === 'Unknown Agent';
  const displayText = isUnassigned ? 'Unassigned' : (name || '—');
  return (
    <span
      role={typeof onClick === 'function' ? 'button' : undefined}
      tabIndex={typeof onClick === 'function' ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => { if (typeof onClick === 'function' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: isUnassigned ? T.borderL : T.greenSf,
        color: isUnassigned ? T.muted : T.ink2,
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 9px 3px 7px',
        borderRadius: 99,
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: typeof onClick === 'function' ? 'pointer' : 'default',
        border: `1px solid ${isUnassigned ? T.border : 'transparent'}`,
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => {
        if (typeof onClick !== 'function') return;
        e.currentTarget.style.background = isUnassigned ? '#e8eeeb' : T.greenLt;
        e.currentTarget.style.borderColor = isUnassigned ? T.muted : T.green;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isUnassigned ? T.borderL : T.greenSf;
        e.currentTarget.style.borderColor = isUnassigned ? T.border : 'transparent';
      }}
    >
      <span style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        flexShrink: 0,
        background: isUnassigned ? T.muted : T.green,
      }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayText}</span>
    </span>
  );
}

// Tooltip styled as AI summary card (white card, border, shadow)
const summaryCardTooltipSx = {
  bgcolor: T.white,
  color: T.ink2,
  border: `1px solid ${T.border}`,
  borderRadius: 2,
  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
  maxWidth: AI_SUMMARY_CARD_WIDTH,
  p: 0,
};

// Call status column: status pill + (when recording) audio player + summary
function CallStatusCell({ call, CallStatusPillComp }) {
  const hasRecording = call.recording_url && call.recording_length_str && call.recording_length_str !== '0:00' && !['MSD', 'CAN', 'BSY', 'DEC', 'DNR'].includes(call.status);
  const hasInsights = !!call.insights;
  const summary = getSummaryFromCall(call);
  const hasSummary = Boolean(summary);

  const summaryTrigger = (sx = {}) => (
    <Typography
      variant="caption"
      sx={{
        display: '-webkit-box',
        fontStyle: 'italic',
        color: 'text.secondary',
        fontSize: '0.7rem',
        lineHeight: 1.35,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word',
        ...sx,
      }}
    >
      {hasSummary ? summary : 'No summary'}
    </Typography>
  );

  if (!hasRecording) {
    if (hasSummary) {
      return (
        <Tooltip
          title={<AIInsightsSummaryCard call={call} />}
          placement="top-start"
          enterDelay={200}
          leaveDelay={100}
          disableInteractive={false}
          slotProps={{
            popper: { sx: { zIndex: 1600 } },
            tooltip: { sx: summaryCardTooltipSx },
          }}
        >
          <Box
            component="span"
            display="block"
            onClick={(e) => e.stopPropagation()}
            sx={{ minHeight: 32, cursor: 'help' }}
          >
            {summaryTrigger()}
          </Box>
        </Tooltip>
      );
    }
    return <CallStatusPillComp status={call.status} label={call.status_verbose} />;
  }

  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 260 }}>
      {!hasSummary && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <CallStatusPillComp status={call.status} label={call.status_verbose} />
        </Box>
      )}
      <SmartAudioPlayer
        audioUrl={call.recording_url}
        duration={call.recording_length_str}
        sx={{ width: '100%', maxWidth: 240, minWidth: 180 }}
      />
      {(hasInsights || hasSummary) && (
        <Box sx={{ minHeight: 32, cursor: hasSummary ? 'help' : 'default' }}>
          {hasSummary ? (
            <Tooltip
              title={<AIInsightsSummaryCard call={call} />}
              placement="top-start"
              enterDelay={200}
              leaveDelay={100}
              disableInteractive={false}
              slotProps={{
                popper: { sx: { zIndex: 1600 } },
                tooltip: { sx: summaryCardTooltipSx },
              }}
            >
              <Box component="span" display="block">
                {summaryTrigger({ color: 'text.secondary', mt: 0.25 })}
              </Box>
            </Tooltip>
          ) : (
            summaryTrigger({ color: T.muted, mt: 0.25 })
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Main: Sidebar | Table ──────────────────────────────────────────────────
const CallListViewV2 = ({
  calls,
  page,
  rowsPerPage,
  searchKeyword,
  isSearchActive,
  selectedStatus,
  onCallUpdated,
  sortField,
  sortOrder,
  onSort,
  statusCounts = {},
  user,
  sidebarWidth = 200,
  onExport,
}) => {
  const navigate = useNavigate();
  const { appliedFilters } = useCall();

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />;
  };

  const handleViewLead = (leadId) => navigate(`/leads/${leadId}`);

  const callList = calls?.calls && Array.isArray(calls.calls) ? calls.calls : [];
  const hasCalls = callList.length > 0;
  const selectedMeta = getSelectedSidebarMeta(statusCounts, selectedStatus || 'all');

  // Unlinked: show UnlinkedCallListView whether triggered from sidebar or quick filter chip
  const isUnlinkedView = selectedStatus === 'unlinked' || appliedFilters?.unlinked === true;
  if (isUnlinkedView) {
    return (
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Constrain sidebar width: embedded mode uses width 100% of parent; without this wrapper
            the sidebar grows to the full row and the unlinked table panel collapses to 0 width. */}
        <Box sx={{ flexShrink: 0, minWidth: sidebarWidth, maxWidth: sidebarWidth }}>
          <CallSidebarV2
            embedded
            width={sidebarWidth}
            statusCounts={statusCounts}
            user={user}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}>
          <UnlinkedCallListView
            calls={calls}
            searchKeyword={searchKeyword}
            isSearchActive={isSearchActive}
            onCallUpdated={onCallUpdated}
            onExport={onExport}
          />
        </Box>
      </Box>
    );
  }

  // Sidebar | Table inline
  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Sidebar */}
      <Box sx={{ flexShrink: 0, minWidth: sidebarWidth, maxWidth: sidebarWidth }}>
        <CallSidebarV2
          embedded
          width={sidebarWidth}
          statusCounts={statusCounts}
          user={user}
        />
      </Box>

      {/* Table panel */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {hasCalls ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              overflow: 'hidden',
              border: `1px solid ${T.border}`,
              borderLeft: 'none',
              background: T.white,
              boxShadow: 'none',
            }}
          >
            {/* SmartTable header: status-based count + Export */}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                borderBottom: `1px solid ${T.border}`,
                background: T.white,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, color: T.ink2, fontSize: 13, whiteSpace: 'nowrap' }}>
                  {selectedMeta.label}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    minWidth: 22,
                    height: 20,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 99,
                    px: 1,
                    fontSize: 11,
                    fontWeight: 800,
                    backgroundColor: T.bg,
                    color: T.muted,
                  }}
                >
                  {selectedMeta.count ?? 0}
                </Box>
              </Box>

              <Tooltip title={selectedMeta.count > 0 ? `Export ${Number(selectedMeta.count).toLocaleString()} calls to CSV` : 'No calls to export'}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<ExportIcon />}
                    size="small"
                    onClick={onExport}
                    disabled={!onExport || (selectedMeta.count ?? 0) === 0}
                    sx={{
                      height: 32,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  >
                    Export
                  </Button>
                </span>
              </Tooltip>
            </Box>

            <style>{TABLE_ROW_GLOBAL_CSS}</style>
            <style>{TABLE_SCROLLBAR_CSS}</style>
            <style>{`
              .calls-table-wrap-v2 thead th { position: sticky; top: 0; z-index: 2; background: ${T.headerBg} !important; box-shadow: 0 1px 0 ${T.border}; }
            `}</style>
            <Box
              className="smart-table-wrap calls-table-wrap-v2"
              sx={{
                overflowX: 'auto',
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
              }}
            >
              <table style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'separate', borderSpacing: 0, minWidth: 1100 }}>
                <thead>
                  <tr>
                    <SmartTableTH theme={T} width={140}>
                      <Box
                        component="span"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: onSort ? 'pointer' : 'default' }}
                        onClick={(e) => { e.stopPropagation(); onSort && onSort('date_time'); }}
                      >
                        Call Datetime
                        {onSort && renderSortIcon('date_time')}
                      </Box>
                    </SmartTableTH>
                    <SmartTableTH theme={T} width={70}>Duration</SmartTableTH>
                    <SmartTableTH theme={T} width={110}>Call Direction</SmartTableTH>
                    <SmartTableTH theme={T} width={90}>Lead ID</SmartTableTH>
                    <SmartTableTH theme={T} width={160}>Lead Name</SmartTableTH>
                    <SmartTableTH theme={T} width={100}>Lead Stage</SmartTableTH>
                    <SmartTableTH theme={T} width={260}>Call Status</SmartTableTH>
                    <SmartTableTH theme={T} width={120}>Agent</SmartTableTH>
                  </tr>
                </thead>
                <tbody>
                  {callList.map((call) => (
                    <tr
                      key={call.id}
                      className={TABLE_ROW_CLASS}
                    >
                      <SmartTableTD theme={T}>
                        <span style={{ fontSize: 12.5, fontWeight: 500, color: T.ink2, whiteSpace: 'nowrap' }}>{[call.date, call.time].filter(Boolean).join(' ') || '—'}</span>
                      </SmartTableTD>
                      <SmartTableTD theme={T}>
                        {call.call_duration ? (
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{call.call_duration}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>-</Typography>
                        )}
                      </SmartTableTD>
                      <SmartTableTD theme={T}>
                        {call.call_type ? <CallDirectionPill callType={call.call_type} /> : <span style={{ fontSize: 12, color: T.muted }}>—</span>}
                      </SmartTableTD>
                      <SmartTableTD theme={T}>
                        {call.lead?.lead_id ? (
                          <a href={`/leads/${call.lead.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewLead(call.lead?.id); }} style={{ textDecoration: 'none', fontSize: 12.5, fontWeight: 700, color: LEAD_T.green, whiteSpace: 'nowrap' }}>
                            #{call.lead.lead_id}
                          </a>
                        ) : (
                          <span style={{ fontSize: 12, color: T.muted }}>—</span>
                        )}
                      </SmartTableTD>
                      <SmartTableTD theme={T}>
                        {call.lead?.id ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <a href={`/leads/${call.lead.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewLead(call.lead?.id); }} style={{ textDecoration: 'none', fontSize: 12.5, fontWeight: 600, color: LEAD_T.green, display: 'block' }}>
                              {call.lead?.name || 'Unknown Customer'}
                            </a>
                            {call.lead?.is_deleted && (
                              <Box component="span" sx={{ fontSize: 10, fontWeight: 600, px: 1, py: 0.25, borderRadius: 1, bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a' }}>Deleted</Box>
                            )}
                          </Box>
                        ) : (
                          <span style={{ fontSize: 12, color: T.muted }}>{call.lead?.name || 'No lead'}</span>
                        )}
                      </SmartTableTD>
                      <SmartTableTD theme={T}>
                        {call.lead?.id ? <StagePill stage={call.lead?.stage} label={call.lead?.stage_verbose} /> : <span style={{ fontSize: 12, color: T.muted }}>—</span>}
                      </SmartTableTD>
                      <SmartTableTD theme={T} wrap>
                        <CallStatusCell call={call} CallStatusPillComp={CallStatusPill} />
                      </SmartTableTD>
                      <SmartTableTD theme={T} wrap>
                        <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                          <AgentChip
                            name={call.agent?.name || 'Unassigned'}
                            assigned={Boolean(call.agent?.name)}
                            onClick={() => {
                              if (call.agent?.id) window.open(`/reports/agent/${call.agent.id}`, '_blank');
                            }}
                          />
                        </div>
                      </SmartTableTD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        ) : (
          <EmptyCallsPageCard
            title={searchKeyword ? 'No matching call records found' : 'No call records found'}
            description={searchKeyword ? "Try adjusting your search criteria or filters." : "No call records for the selected time period. Try adjusting your date range or filters."}
            isSearchActive={isSearchActive}
            compact={false}
          />
        )}
      </Box>
    </Box>
  );
};

export default CallListViewV2;
